// Visa Eligibility Wizard — shared component used by TH and EN routes.
// Phase P-VISA-WIZARD (2026-06).
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, RotateCcw, Sparkles, ChevronRight, ChevronLeft, Trophy } from "lucide-react";
import {
  EMPTY_ANSWERS,
  WIZARD_QUESTIONS,
  scoreVisas,
  type Locale,
  type WizardAnswers,
} from "@/data/visa-wizard";

interface Props {
  locale: Locale;
}

const COPY = {
  th: {
    step: "ขั้นตอน",
    of: "จาก",
    back: "ย้อนกลับ",
    next: "ถัดไป",
    restart: "เริ่มใหม่",
    seeResults: "ดูผลแนะนำ",
    matchScore: "คะแนนความเหมาะสม",
    bestMatch: "วีซ่าที่เหมาะที่สุด",
    alsoConsider: "ทางเลือกอื่นที่น่าพิจารณา",
    notRecommended: "ไม่แนะนำสำหรับโปรไฟล์ของคุณ",
    viewDetail: "ดูรายละเอียดเต็ม",
    consult: "ปรึกษาทีม NYC",
    nycFee: "ค่าบริการ NYC",
    govFee: "ค่าธรรมเนียมราชการ",
    serviceTime: "ระยะเวลา",
    workRights: "สิทธิทำงาน",
    finance: "หลักฐานการเงิน",
    reasons: "เหตุผลที่แนะนำ",
    badge: "AI Eligibility Wizard · 5 คำถาม · ~60 วินาที",
    quickAnswerH2: "ระบบช่วยเลือกวีซ่าทำงานยังไง?",
    quickAnswerBody:
      "ระบบจะถามโปรไฟล์ของคุณ 5 ข้อ (เป้าหมาย, ระยะเวลาพัก, งาน, อายุ, หลักฐานการเงิน) แล้วให้คะแนนแต่ละวีซ่า (Tourist · DTV · LTR · Non-O Retirement · Non-O Marriage · Non-B + Work Permit · Non-ED) ใน 60 วินาที พร้อมเหตุผลและข้อกำหนดเงินทุน",
    relatedTitle: "บริการที่เกี่ยวข้อง",
    related: [
      { t: "ตารางเทียบ 7 วีซ่า", d: "Tourist · DTV · LTR · Non-O · Non-B · Non-ED", to: "/visa-matrix" as const },
      { t: "DTV (Digital Nomad)", d: "วีซ่า 5 ปี · 9 หมวด", to: "/dtv" as const },
      { t: "LTR (BOI 10 ปี)", d: "HSP 17% · Pensioner · HNWI", to: "/ltr" as const },
      { t: "Work Permit", d: "ลูกจ้างบริษัทไทย", to: "/work-permit" as const },
      { t: "Company Setup", d: "Thai Limited · BOI · Branch", to: "/company-setup" as const },
      { t: "TM30 / 90-day", d: "Compliance ตลอด journey", to: "/visa-compliance" as const },
    ],
  },
  en: {
    step: "Step",
    of: "of",
    back: "Back",
    next: "Next",
    restart: "Start over",
    seeResults: "See recommendations",
    matchScore: "Match score",
    bestMatch: "Best-fit visa",
    alsoConsider: "Also consider",
    notRecommended: "Not recommended for your profile",
    viewDetail: "Full details",
    consult: "Talk to NYC",
    nycFee: "NYC service fee",
    govFee: "Government fee",
    serviceTime: "Processing",
    workRights: "Work rights",
    finance: "Financial proof",
    reasons: "Why we recommend it",
    badge: "AI Eligibility Wizard · 5 questions · ~60 seconds",
    quickAnswerH2: "How does the visa wizard work?",
    quickAnswerBody:
      "Answer 5 quick questions (purpose, stay length, work, age, financial standing) and the wizard scores all 7 long-stay Thai visas (Tourist · DTV · LTR · Non-O Retirement · Non-O Marriage · Non-B + Work Permit · Non-ED) in 60 seconds, with reasons and financial thresholds.",
    relatedTitle: "Related services",
    related: [
      { t: "7-visa comparison matrix", d: "Tourist · DTV · LTR · Non-O · Non-B · Non-ED", to: "/en/visa-matrix" as const },
      { t: "DTV (Digital Nomad)", d: "5-year visa across 9 categories", to: "/en/dtv" as const },
      { t: "LTR (BOI 10-year)", d: "HSP 17% PIT · Pensioner · HNWI", to: "/en/ltr" as const },
      { t: "Work Permit", d: "All nationalities · Thai employers", to: "/en/work-permit" as const },
      { t: "Company Setup", d: "Thai Limited · BOI · Branch", to: "/en/company-setup" as const },
      { t: "TM30 / 90-day", d: "Compliance throughout the journey", to: "/en/visa-compliance" as const },
    ],
  },
};

export function VisaWizard({ locale }: Props) {
  const t = COPY[locale];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<WizardAnswers>(EMPTY_ANSWERS);
  const [showResults, setShowResults] = useState(false);

  const total = WIZARD_QUESTIONS.length;
  const currentQ = WIZARD_QUESTIONS[step];
  const currentAnswer = answers[currentQ.id];
  const isLast = step === total - 1;

  const results = useMemo(() => scoreVisas(answers), [answers]);
  const top = results[0];
  const others = results.slice(1, 4);
  const notFit = results.slice(4);

  const choose = (value: string) => {
    setAnswers((a) => ({ ...a, [currentQ.id]: value as WizardAnswers[typeof currentQ.id] }));
  };

  const next = () => {
    if (isLast) {
      setShowResults(true);
      // smooth scroll for context
      requestAnimationFrame(() => window.scrollTo({ top: 320, behavior: "smooth" }));
    } else {
      setStep((s) => Math.min(s + 1, total - 1));
    }
  };

  const back = () => {
    if (showResults) {
      setShowResults(false);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const restart = () => {
    setAnswers(EMPTY_ANSWERS);
    setStep(0);
    setShowResults(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const progress = showResults ? 100 : ((step + (currentAnswer ? 1 : 0)) / total) * 100;

  return (
    <div className="rounded-3xl border border-border bg-card shadow-sm">
      {/* Progress */}
      <div className="border-b border-border px-5 py-4 sm:px-7">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>
            {showResults ? (locale === "th" ? "ผลแนะนำ" : "Recommendation") : `${t.step} ${step + 1} ${t.of} ${total}`}
          </span>
          <span className="inline-flex items-center gap-1 text-primary">
            <Sparkles className="h-3 w-3" /> {Math.round(progress)}%
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Body */}
      {!showResults ? (
        <div className="p-5 sm:p-7">
          <h2 className="text-xl font-extrabold sm:text-2xl">
            {locale === "th" ? currentQ.questionTh : currentQ.questionEn}
          </h2>
          <div className="mt-5 grid gap-2.5">
            {currentQ.options.map((opt) => {
              const selected = currentAnswer === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => choose(opt.value)}
                  className={`group flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-background hover:border-primary/60"
                  }`}
                  aria-pressed={selected}
                >
                  <span
                    className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                      selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                    }`}
                  >
                    {selected && <CheckCircle2 className="h-4 w-4" />}
                  </span>
                  <span className="text-sm font-semibold sm:text-base">
                    {locale === "th" ? opt.labelTh : opt.labelEn}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={back}
              disabled={step === 0}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> {t.back}
            </button>
            <button
              onClick={next}
              disabled={!currentAnswer}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-40"
            >
              {isLast ? t.seeResults : t.next}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="p-5 sm:p-7 space-y-6">
          {/* Top result */}
          <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Trophy className="h-3 w-3" /> {t.bestMatch}
            </div>
            <div className="mt-3 flex items-start gap-4">
              <div className="text-4xl">{top.visa.icon}</div>
              <div className="flex-1">
                <h3 className="text-xl font-extrabold sm:text-2xl">
                  {locale === "th" ? top.visa.nameTh : top.visa.nameEn}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {locale === "th" ? top.visa.taglineTh : top.visa.taglineEn}
                </p>
                <p className="mt-3 text-sm leading-relaxed">
                  {locale === "th" ? top.visa.quickAnswerTh : top.visa.quickAnswerEn}
                </p>
              </div>
              <div className="hidden text-right sm:block">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.matchScore}</div>
                <div className="text-3xl font-extrabold text-primary">{Math.max(top.score, 0)}</div>
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              <div>
                <dt className="font-bold text-muted-foreground">{t.govFee}</dt>
                <dd className="mt-0.5 font-semibold">{locale === "th" ? top.visa.govFeeTh : top.visa.govFeeEn}</dd>
              </div>
              <div>
                <dt className="font-bold text-muted-foreground">{t.nycFee}</dt>
                <dd className="mt-0.5 font-extrabold text-primary">
                  THB {top.visa.servicePriceTHB.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="font-bold text-muted-foreground">{t.serviceTime}</dt>
                <dd className="mt-0.5 font-semibold">{top.visa.processingDays}</dd>
              </div>
              <div>
                <dt className="font-bold text-muted-foreground">{t.workRights}</dt>
                <dd className="mt-0.5 font-semibold">{locale === "th" ? top.visa.workTh : top.visa.workEn}</dd>
              </div>
            </dl>

            {(locale === "th" ? top.reasonsTh : top.reasonsEn).length > 0 && (
              <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.reasons}</div>
                <ul className="mt-1.5 space-y-1 text-xs">
                  {(locale === "th" ? top.reasonsTh : top.reasonsEn).map((r) => (
                    <li key={r} className="flex items-start gap-1.5">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={(locale === "th" ? top.visa.detailPath.th : top.visa.detailPath.en) as never}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90"
              >
                {t.viewDetail} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <a
                href="tel:+66922429293"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-bold hover:bg-muted"
              >
                {t.consult}
              </a>
            </div>
          </div>

          {/* Also consider */}
          {others.length > 0 && (
            <div>
              <h3 className="text-base font-extrabold sm:text-lg">{t.alsoConsider}</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {others.map((r) => (
                  <Link
                    key={r.visa.slug}
                    to={(locale === "th" ? r.visa.detailPath.th : r.visa.detailPath.en) as never}
                    className="block rounded-xl border border-border bg-background p-3 hover:border-primary transition"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-2xl">{r.visa.icon}</span>
                      <span className="text-xs font-bold text-primary">{Math.max(r.score, 0)}</span>
                    </div>
                    <div className="mt-2 text-sm font-extrabold">
                      {locale === "th" ? r.visa.nameTh : r.visa.nameEn}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2">
                      {locale === "th" ? r.visa.taglineTh : r.visa.taglineEn}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Not recommended */}
          {notFit.length > 0 && (
            <details className="rounded-xl border border-border bg-muted/30 p-3">
              <summary className="cursor-pointer text-xs font-bold text-muted-foreground">
                {t.notRecommended} ({notFit.length})
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {notFit.map((r) => (
                  <li key={r.visa.slug} className="flex items-center justify-between">
                    <span>
                      {r.visa.icon} {locale === "th" ? r.visa.nameTh : r.visa.nameEn}
                    </span>
                    <span>{r.score}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <button
              onClick={back}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" /> {t.back}
            </button>
            <button
              onClick={restart}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-bold hover:bg-muted"
            >
              <RotateCcw className="h-4 w-4" /> {t.restart}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function WizardRelatedServices({ locale }: { locale: Locale }) {
  const t = COPY[locale];
  return (
    <section className="px-4 py-12">
      <div className="mx-auto max-w-5xl">
        <h2 className="text-2xl font-extrabold sm:text-3xl">{t.relatedTitle}</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {t.related.map((s) => (
            <Link key={s.t} to={s.to as never} className="rounded-xl border border-border bg-card p-4 hover:border-primary transition">
              <div className="font-bold text-sm">{s.t}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export { COPY as WIZARD_COPY };
